import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useObserver } from 'mobx-react-lite';

import { ARIA_ROLES } from 'shared/constants';
import useTranslation from 'hooks/use-translation';
import { useDutchiePay } from 'src/payments/hooks';

import { formatName } from 'shared/helpers/users';

import UserIcon from 'assets/user-icon';
import UnionIcon from 'assets/union-icon';
import EmailIcon from 'assets/email-icon';
import { MoneyIcon } from 'assets/money-icon';

import { VerticalTabs, VerticalTab } from 'components/core';
import { TabLink } from 'src/components/core/tabs/tab-link';
import ROUTES from 'src/routes';

const { ACCOUNT, PREFERENCES, ORDERS, BANK_ACCOUNT } = ROUTES;
const ANCHOR_COMPONENT = `a`;

export default function AccountSideNavBar(props) {
  const { selectedTab, user } = props;
  const { t } = useTranslation();
  const { shouldShowBankAccountTab } = useDutchiePay();

  const userName = useObserver(() => formatName(user.profile));
  const joinDate = useObserver(() => user.createdAt) || `2017-07-15T00:00:00.000Z`;
  const joinedString = moment(joinDate).format(`MMMM, YYYY`);

  return (
    <SideNavBarContainer>
      <Header>
        <HeaderText data-private>{userName}</HeaderText>
        {joinDate && (
          <HeaderSubtext>
            {t('accountPage.joined', 'Joined')} {joinedString}
          </HeaderSubtext>
        )}
      </Header>

      <StyledTabs data-testid='account-navigation-tabs' selectionFollowsFocus value={selectedTab}>
        {shouldShowBankAccountTab && (
          <TabLink value={BANK_ACCOUNT} href={BANK_ACCOUNT}>
            <StyledTab
              id='account-tab-bank-account'
              aria-controls='account-tab-panel-bank-account'
              aria-selected={selectedTab === BANK_ACCOUNT}
              component={ANCHOR_COMPONENT}
              icon={MoneyIcon}
              label={t('accountPage.bankAccount', 'Bank Account')}
              role={ARIA_ROLES.TAB}
              value={BANK_ACCOUNT}
            />
          </TabLink>
        )}
        <TabLink value={ACCOUNT} href={ACCOUNT}>
          <StyledTab
            id='account-tab-my-account'
            data-testid='account-tab-my-account'
            aria-controls='account-tab-panel-my-account'
            aria-selected={selectedTab === ACCOUNT}
            component={ANCHOR_COMPONENT}
            icon={UserIcon}
            label={t('accountPage.accountDetails', 'Account Details')}
            role={ARIA_ROLES.TAB}
            value={ACCOUNT}
          />
        </TabLink>
        <TabLink value={PREFERENCES} href={PREFERENCES}>
          <StyledTab
            id='account-tab-preferences'
            data-testid='account-tab-preferences'
            aria-controls='account-tab-panel-preferences'
            aria-selected={selectedTab === PREFERENCES}
            component={ANCHOR_COMPONENT}
            icon={EmailIcon}
            label={t('accountPage.preferences', 'Preferences')}
            role={ARIA_ROLES.TAB}
            value={PREFERENCES}
          />
        </TabLink>
        <TabLink value={ORDERS} href={ORDERS}>
          <StyledTab
            id='account-tab-orders'
            data-testid='account-tab-orders'
            aria-controls='account-tab-panel-orders'
            aria-selected={selectedTab === ORDERS}
            component={ANCHOR_COMPONENT}
            icon={UnionIcon}
            label={t('accountPage.orders', 'Orders')}
            role={ARIA_ROLES.TAB}
            value={ORDERS}
          />
        </TabLink>
      </StyledTabs>
    </SideNavBarContainer>
  );
}

const SideNavBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 50px;
  min-width: 279px;
`;

const Header = styled.div`
  padding-left: 71px;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const HeaderText = styled.h2`
  line-height: 120%;
  font-weight: bold;
  font-size: 19px;
`;

const HeaderSubtext = styled.p`
  line-height: 165%;
  font-size: 13px;
`;

const StyledTabs = styled(VerticalTabs)`
  margin-top: 30px;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  &.MuiTabs-root {
    box-shadow: none !important;
  }
`;

const StyledTab = styled(VerticalTab)`
  .MuiTab-wrapper {
    margin-left: 39px;
    align-items: flex-start;
  }
`;
